import React, { useState, useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';

interface Building {
  id: number;
  position: [number, number, number];
  size: [number, number, number];
}

function CityGrid() {
  // Generate 24 static building coordinates
  const buildings = useMemo<Building[]>(() => {
    const list: Building[] = [];
    const r = new THREE.Vector2();
    for (let i = 0; i < 28; i++) {
      const x = (Math.random() - 0.5) * 32;
      const z = (Math.random() - 0.5) * 32;
      
      // Avoid placing buildings directly in the center under the star
      if (Math.abs(x) < 3.5 && Math.abs(z) < 3.5) continue;
      
      const w = 0.6 + Math.random() * 1.4;
      const h = 0.8 + Math.random() * 3.5;
      const d = 0.6 + Math.random() * 1.4;
      list.push({
        id: i,
        position: [x, h / 2, z],
        size: [w, h, d]
      });
    }
    return list;
  }, []);

  return (
    <group>
      {/* Flat base city floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
        <planeGeometry args={[40, 40]} />
        <meshStandardMaterial color="#1E293B" roughness={0.9} />
      </mesh>

      {/* City streets grid lines */}
      <gridHelper args={[40, 40, '#38BDF8', '#1E3A5F']} position={[0, 0.01, 0]} />

      {/* Buildings */}
      {buildings.map((b) => (
        <mesh key={b.id} position={b.position}>
          <boxGeometry args={b.size} />
          <meshStandardMaterial color="#475569" roughness={0.7} metalness={0.2} />
        </mesh>
      ))}
    </group>
  );
}

function NeutronStar() {
  const nsRef = useRef<THREE.Mesh>(null);

  useFrame(() => {
    if (nsRef.current) {
      nsRef.current.rotation.y += 0.03;
      nsRef.current.rotation.z += 0.005;
    }
  });

  return (
    <mesh ref={nsRef} position={[0, 5, 0]}>
      <sphereGeometry args={[0.5, 32, 32]} />
      <meshStandardMaterial
        color="#FFFFFF"
        emissive="#60A5FA"
        emissiveIntensity={3.0}
        roughness={0.15}
      />
      <pointLight intensity={5} color="#60A5FA" distance={20} decay={1.5} />

      {/* Floating 3D Label */}
      <Html distanceFactor={15} position={[0, 0.9, 0]}>
        <div className="bg-[#0b0f19]/95 border border-white/10 px-2 py-0.5 rounded text-[9px] font-bold text-white whitespace-nowrap shadow-lg select-none pointer-events-none">
          Neutron Star (20km radius)
        </div>
      </Html>
    </mesh>
  );
}

function SunComparison({ compareSun }: { compareSun: boolean }) {
  const sunRef = useRef<THREE.Mesh>(null);

  useFrame((state, delta) => {
    if (sunRef.current) {
      // Lerp scale and position based on state
      const targetScale = compareSun ? 1.0 : 0.0;
      sunRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), delta * 3.0);
      
      const targetY = compareSun ? 50 : 100;
      sunRef.current.position.y = THREE.MathUtils.lerp(sunRef.current.position.y, targetY, delta * 3.0);
      
      sunRef.current.rotation.y += delta * 0.05;
    }
  });

  return (
    <mesh ref={sunRef} position={[0, 100, -35]}>
      <sphereGeometry args={[35, 64, 64]} />
      <meshStandardMaterial
        color="#FFA500"
        emissive="#F97316"
        emissiveIntensity={1.0}
      />
      
      {/* Sun Label floating in front of it */}
      {compareSun && (
        <Html distanceFactor={60} position={[0, 38, 0]}>
          <div className="bg-[#0b0f19]/95 border border-white/10 px-2.5 py-1 rounded text-[10px] font-bold text-orange-400 whitespace-nowrap shadow-lg select-none pointer-events-none">
            Our Sun (1.4 million km)
          </div>
        </Html>
      )}
    </mesh>
  );
}

function CameraZoomController({ zoom, compareSun }: { zoom: number; compareSun: boolean }) {
  useFrame((state, delta) => {
    // Zoom ranges: slider zoom (0-100) or auto-zoom if compareSun is active
    const effectiveZoom = compareSun ? Math.max(zoom, 80) : zoom;
    const targetDistance = 22 + (effectiveZoom / 100) * 138; // 22 to 160 units distance
    
    const camera = state.camera;
    const dir = camera.position.clone().normalize();
    camera.position.copy(dir.multiplyScalar(targetDistance));
  });
  return null;
}

export function NeutronStarCitySize() {
  const [zoom, setZoom] = useState<number>(0);
  const [compareSun, setCompareSun] = useState<boolean>(false);

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <Canvas camera={{ position: [0, 18, 25], fov: 45 } as any}>
        <color attach="background" args={['#0F172A']} />
        <ambientLight intensity={0.15} />
        
        {/* Sky light */}
        <directionalLight position={[10, 25, 10]} intensity={0.4} />

        <CityGrid />
        <NeutronStar />
        <SunComparison compareSun={compareSun} />

        <CameraZoomController zoom={zoom} compareSun={compareSun} />
        <OrbitControls enableZoom={true} enablePan={false} maxDistance={200} minDistance={10} />
      </Canvas>

      {/* Info Panel Top Right */}
      <div className="absolute top-3 right-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1 pointer-events-none select-none text-white w-[190px]">
        <span className="text-[9px] text-white/40 tracking-wider uppercase font-bold">Stellar Metrics</span>
        <div className="flex flex-col gap-0.5 text-[10px] leading-tight">
          <span className="font-semibold text-cyan-400">Neutron Star: ~20km across</span>
          <span className="text-white/80">Density: 1 tsp = 1 billion tons</span>
          <span className="text-white/80">Gravity: 2 billion× Earth's</span>
        </div>
      </div>

      {/* Title Header Top Left */}
      <div className="absolute top-3 left-3 pointer-events-none select-none text-white/40 text-sm font-medium">
        Stellar Scale Comparison
      </div>

      {/* City Scale Indicator Bottom Left */}
      <div className="absolute bottom-16 left-3 bg-[#0b0f19]/80 backdrop-blur-sm px-2.5 py-1.5 rounded-lg border border-white/10 text-[9px] font-semibold text-white/70 select-none pointer-events-none">
        City Grid: London / New York (20km diameter)
      </div>

      {/* Compare Button & Zoom Slider Bottom Center */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex items-center gap-4">
        {/* Zoom Scroller */}
        <div className="flex-1 flex flex-col gap-1">
          <span className="text-white text-[9px] uppercase font-bold tracking-wider opacity-60">
            Scale Zoom: {zoom.toFixed(0)}%
          </span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-white/20 rounded-lg appearance-none"
          />
        </div>

        {/* Compare Button */}
        <button
          onClick={() => {
            setCompareSun(!compareSun);
            if (!compareSun) {
              setZoom(85); // Auto zoom out to see the Sun
            }
          }}
          className={`px-3 py-2 rounded-lg font-bold text-xs uppercase tracking-wide transition-all border ${
            compareSun
              ? 'bg-orange-500 hover:bg-orange-600 border-orange-500 text-white shadow-lg shadow-orange-500/20'
              : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'
          }`}
        >
          ↔ Compare with Sun
        </button>
      </div>
    </div>
  );
}
